import {
  faCircleInfo,
  faExclamationCircle,
  faExclamationTriangle,
} from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';

import {Icon} from '../Icon/Icon';
import {Text} from '../Text';

import type {AppErrorKind} from '../../utils/errors';
import type {IconDefinition} from '@fortawesome/pro-regular-svg-icons';

type KindConfig = {
  icon: IconDefinition;
  containerClassName: string;
  iconClassName: string;
  textClassName: string;
};

const INFO_BOX_KIND_CONFIG: Record<AppErrorKind, KindConfig> = {
  critical: {
    icon: faExclamationCircle,
    containerClassName: 'border-tomato6 bg-tomato2',
    iconClassName: 'text-tomato9',
    textClassName: 'text-tomato11',
  },
  warning: {
    icon: faExclamationTriangle,
    containerClassName: 'border-amber6 bg-amber2',
    iconClassName: 'text-amber9',
    textClassName: 'text-amber11',
  },
  informative: {
    icon: faCircleInfo,
    containerClassName: 'border-blue6 bg-blue2',
    iconClassName: 'text-blue9',
    textClassName: 'text-blue11',
  },
};

type Props = Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> & {
  kind?: AppErrorKind;
  icon?: IconDefinition;
  action?: React.ReactNode;
  children: React.ReactNode;
};
export function InfoBox({
  kind = 'informative',
  icon,
  action,
  className,
  children,
  ...props
}: Props): React.JSX.Element {
  const config = INFO_BOX_KIND_CONFIG[kind];

  return (
    <div
      {...props}
      className={clsx(
        'grid grid-cols-[auto_1fr_auto] items-center gap-3 rounded-lg border px-4 py-3',
        config.containerClassName,
        className
      )}
    >
      <Icon
        icon={icon ?? config.icon}
        className={clsx(config.iconClassName, 'self-start', action ? 'mt-2.5' : 'mt-0.75')}
      />
      <Text as="small" className={config.textClassName}>
        {children}
      </Text>
      {action}
    </div>
  );
}

InfoBox.displayName = 'InfoBox';
